const isColorShellItem = (item) => {
  if (!item) return false;
  
  if (typeof window.isColorShellItem === 'function') {
    return window.isColorShellItem(item);
  }
  
  // Check if item has _upgrade property
  let hasUpgrade = false;
  if (item.properties) {
    if (Array.isArray(item.properties)) {
      hasUpgrade = item.properties.some(prop => prop.name === '_upgrade');
    } else if (typeof item.properties === 'object') {
      hasUpgrade = !!item.properties._upgrade;
    }
  }

  // Check by SKU (color products have specific SKUs)
  const sku = item.sku || '';
  const isColorSku = sku === '102010' || sku === '102011' || sku === '100401' || sku === '300037' ||
                    sku.startsWith('1020') || sku.startsWith('1004') || sku.startsWith('3000');

  // Check by title containing "Color Upgrade" or "Upgrade"
  const title = (item.title || '').toLowerCase();
  const isColorUpgradeTitle = title.includes('color upgrade') || title.includes('shell color');

  // Check by handle
  const handle = (item.handle || '').toLowerCase();
  const knownColorHandles = [
    'cloak-black',
    'cloak-recon-haze-topo',
    'cloak-mc-alpine',
    'cloak-mc-arid',
    'cloak-mc-tropic',
    'cloak-mc-black',
    'cloak-arctic-white',
    'cloak-safety-yellow',
    'cloak-neon-pink',
    'cloak-safety-orange',
    'cloak-purple',
    'cloak-police-blue',
    'cloak-army-green',
    'cloak-black-carbon-fiber',
    'cloak-dark-grey'
  ];
  const isColorHandle = knownColorHandles.includes(handle);

  return hasUpgrade || isColorSku || isColorUpgradeTitle || isColorHandle;
};

// Only Cloak, Low-Pro and the bag bundle take color shells (allowlist by handle).
// Defers to color-shell-selector.js when it is loaded.
const isShellEligibleHandle = (handle) => {
  if (typeof window.isShellEligibleHandle === 'function') {
    return window.isShellEligibleHandle(handle);
  }
  handle = (handle || '').toLowerCase();
  if (handle.includes('mag-carrier')) return false;
  // Parts and holster bases have no shell picker and never take a shell
  if (handle.includes('replacement-parts') || handle.includes('holster-base')) return false;
  return handle.startsWith('cloak-') || handle.startsWith('low-pro-') || handle === 'bag-bundle';
};

const isEligibleMainProduct = (item) => {
  if (!item || isColorShellItem(item)) return false;
  return isShellEligibleHandle(item.handle);
};

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => {
          if (!response.ok) return;
          return response.text();
        })
        .then((responseText) => {
          if (!responseText) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => {
          if (!response.ok) return;
          return response.text();
        })
        .then((responseText) => {
          if (!responseText) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);
    let holsterItem = null;

    // Check if this is a color shell item and enforce quantity of 1
    // Also check if this is a holster being deleted so we can clean up color shells
    const checkAndUpdate = async () => {
      try {
        const cartResponse = await fetch('/cart.js?t=' + Date.now());
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          const itemIndex = parseInt(line) - 1;
          if (cart.items && cart.items[itemIndex]) {
            const item = cart.items[itemIndex];
            holsterItem = item;
            const isColorShell = isColorShellItem(item);
            const isTitleBasedColorShell = isColorShell;
            const isColorShellSku = isColorShell;
            const hasUpgradeProperty = isColorShell;
            const itemTitle = item.title || '';

            // Enforce quantity matching the linked holster for color shells
            if (isColorShell && quantity > 0) {
              let expectedQty = 1;
              let mainProductKey = null;
              if (item.properties) {
                if (Array.isArray(item.properties)) {
                  const mainProductProp = item.properties.find(prop => prop.name === '_main_product_key');
                  mainProductKey = mainProductProp ? mainProductProp.value : null;
                } else if (typeof item.properties === 'object') {
                  mainProductKey = item.properties._main_product_key || null;
                }
              }
              if (!mainProductKey) {
                try {
                  const localData = localStorage.getItem('cloak-color-shell-metadata');
                  if (localData) {
                    const metadata = JSON.parse(localData);
                    mainProductKey = metadata[item.key]?.mainProductKey || null;
                  }
                } catch (e) {}
              }
              if (mainProductKey) {
                const linkedHolster = cart.items.find(h => h.key === mainProductKey || `${h.product_id}:${h.variant_id}` === mainProductKey);
                if (linkedHolster) {
                  expectedQty = linkedHolster.quantity;
                }
              } else {
                // Fallback: use first holster
                const holsterItems = cart.items.filter(cartItem => isEligibleMainProduct(cartItem));
                if (holsterItems.length > 0) {
                  expectedQty = holsterItems[0].quantity;
                }
              }
              
              if (quantity !== expectedQty) {
                quantity = expectedQty;
              }
            }

            // If updating/removing a holster, find any linked shells and sync their quantity in the same request
            const handle = item.product_handle || item.handle || '';
            const isCloakProduct = handle.startsWith('cloak-') && !isColorShell;
            const isMagCarrier = handle.includes('mag-carrier') || itemTitle.toLowerCase().includes('mag carrier');

            const isHolster = (itemTitle.includes('Cloak') || itemTitle.includes('Low-Pro') || isCloakProduct) &&
                            !isColorShell && !isMagCarrier;

            if (isHolster) {
              // Find any shells linked to this holster
              const holsterStableKey = `${item.product_id}:${item.variant_id}`;
              const shellsToUpdate = cart.items.filter(cartItem => {
                if (!isColorShellItem(cartItem)) return false;
                
                let itemMainKey = null;
                if (cartItem.properties) {
                  if (Array.isArray(cartItem.properties)) {
                    const mainProductProp = cartItem.properties.find(prop => prop.name === '_main_product_key');
                    itemMainKey = mainProductProp ? mainProductProp.value : null;
                  } else if (typeof cartItem.properties === 'object') {
                    itemMainKey = cartItem.properties._main_product_key || null;
                  }
                }
                if (!itemMainKey) {
                  try {
                    const localData = localStorage.getItem('cloak-color-shell-metadata');
                    if (localData) {
                      const metadata = JSON.parse(localData);
                      itemMainKey = metadata[cartItem.key]?.mainProductKey || null;
                    }
                  } catch (e) {}
                }
                
                return itemMainKey === item.key || itemMainKey === holsterStableKey;
              });

              if (shellsToUpdate.length > 0) {
                const updates = {};
                updates[item.key] = quantity;
                shellsToUpdate.forEach(shell => {
                  updates[shell.key] = quantity;
                });
                
                const body = JSON.stringify({
                  updates,
                  sections: this.getSectionsToRender().map((section) => section.section),
                  sections_url: window.location.pathname,
                });
                
                if (quantity === 0) {
                  window.holsterBeingRemoved = false;
                  window.deletedHolsterKey = null;
                }
                console.log('[Cart] Batch updating holster and linked shells:', updates);
                return fetch('/cart/update.js', { ...fetchConfig(), ...{ body } });
              }

              if (quantity === 0) {
                // Fallback to sequential removal if not batch-removed
                const stableKey = `${item.product_id}:${item.variant_id}`;
                window.holsterBeingRemoved = true;
                window.deletedHolsterKey = stableKey;
              }
            }

              // If removing a color shell, queue its main product (holster) for removal
              if (isColorShell || isTitleBasedColorShell) {
                let mainProductKey = null;
                if (item.properties) {
                  if (Array.isArray(item.properties)) {
                    const mainProductProp = item.properties.find(prop => prop.name === '_main_product_key');
                    mainProductKey = mainProductProp ? mainProductProp.value : null;
                  } else if (typeof item.properties === 'object') {
                    mainProductKey = item.properties._main_product_key || null;
                  }
                }
                
                // Fallback: Read directly from localStorage metadata
                if (!mainProductKey) {
                  try {
                    const localData = localStorage.getItem('cloak-color-shell-metadata');
                    if (localData) {
                      const metadata = JSON.parse(localData);
                      mainProductKey = metadata[item.key]?.mainProductKey || null;
                      console.log('[Cart] Read mainProductKey from localStorage:', mainProductKey);
                    }
                  } catch (e) {
                    console.error('[Cart] Error parsing localStorage metadata:', e);
                  }
                }

                if (!mainProductKey && typeof window.getColorShellMainProductKey === 'function') {
                  mainProductKey = window.getColorShellMainProductKey(item.key);
                }
                
                if (mainProductKey) {
                  const holsterToRemove = cart.items.find(cartItem => {
                    const stableKey = `${cartItem.product_id}:${cartItem.variant_id}`;
                    return cartItem.key === mainProductKey || stableKey === mainProductKey;
                  });

                  if (holsterToRemove) {
                    const updates = {};
                    updates[item.key] = 0;
                    updates[holsterToRemove.key] = 0;

                    const body = JSON.stringify({
                      updates,
                      sections: this.getSectionsToRender().map((section) => section.section),
                      sections_url: window.location.pathname,
                    });

                    window.shellBeingRemoved = false;
                    window.deletedMainProductKey = null;
                    console.log('[Cart] Batch removing shell and linked holster:', updates);
                    return fetch('/cart/update.js', { ...fetchConfig(), ...{ body } });
                  }

                  // Fallback to sequential removal
                  window.shellBeingRemoved = true;
                  window.deletedMainProductKey = mainProductKey;
                  console.log('[Cart] Shell being removed. Linked holster key:', mainProductKey);
                }
              }
            }
          }
        } catch (error) {
        // If cart fetch fails, continue with original quantity
      }

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });
    };

    checkAndUpdate()
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedItem = holsterItem ? parsedState.items.find(i => i.key === holsterItem.key) : null;
        const updatedValue = updatedItem ? updatedItem.quantity : (parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined);
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        // If a holster was just removed, clean up its color shells
        if (window.holsterBeingRemoved) {
          const deletedKey = window.deletedHolsterKey;
          this.removeColorShells(deletedKey);
        }

        // If a shell was just removed, clean up its linked holster
        if (window.shellBeingRemoved) {
          const mainKey = window.deletedMainProductKey;
          this.removeHolster(mainKey);
        }

        // Clean up orphaned color shells after cart update
        // IMPORTANT: Only run cleanup when deleting cloak-single or cloak-chest products
        const isDeletion = quantity === 0;
        const isAddingColorUpgrade = window.addingColorUpgrades && window.addingColorUpgrades.size > 0;

        // Check if the deleted item is a cloak holster or bag bundle
        let isDeletedItemCloakHolster = false;
        if (isDeletion) {
          try {
            // Check stored deleted item info first
            if (window.lastDeletedItem) {
              isDeletedItemCloakHolster = isShellEligibleHandle(window.lastDeletedItem.handle);
              window.lastDeletedItem = null; // Clear after use
            } else {
              // Fallback to DOM detection if no stored info
              const cartItemElement = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
              if (cartItemElement) {
                const itemLink = cartItemElement.querySelector('a[href*="/products/"]');
                if (itemLink) {
                  const handleMatch = itemLink.getAttribute('href').match(/\/products\/([^/?#]+)/);
                  // Check if it's a cloak holster or bag bundle
                  isDeletedItemCloakHolster = !!handleMatch && isShellEligibleHandle(handleMatch[1]);
                }
              }
            }
          } catch (e) {
            // Could not determine deleted item handle
          }
        }
        
        // Get previous item count from the items we had before the update
        const previousItemCount = items.length;
        const newItemCount = parsedState.items ? parsedState.items.length : 0;
        const itemCountIncreased = newItemCount > previousItemCount;
        
        // isColorShellItem helper is defined at the top of the file
        
        // Helper function to remove cart item by key and update UI
        const removeCartItemByKey = async (itemKey) => {
          try {
            const cartResponse = await fetch('/cart.js?t=' + Date.now());
            if (!cartResponse.ok) return;
            
            const cart = await cartResponse.json();
            const item = cart.items.find(i => i.key === itemKey);
            if (!item) return;
            
            const line = cart.items.indexOf(item) + 1;
            const changeResponse = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                line: line, 
                quantity: 0,
                sections: this.getSectionsToRender().map((section) => section.section),
                sections_url: window.location.pathname
              })
            });
            
            if (changeResponse.ok) {
              const cartData = await changeResponse.json();
              
              // Update cart UI
              const cartDrawer = document.querySelector('cart-drawer');
              const cartFooter = document.getElementById('main-cart-footer');
              
              if (cartFooter) cartFooter.classList.toggle('is-empty', cartData.item_count === 0);
              if (cartDrawer) cartDrawer.classList.toggle('is-empty', cartData.item_count === 0);
              
              // Update cart sections
              this.getSectionsToRender().forEach((section) => {
                const elementToReplace =
                  document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
                if (elementToReplace && cartData.sections && cartData.sections[section.section]) {
                  elementToReplace.innerHTML = this.getSectionInnerHTML(
                    cartData.sections[section.section],
                    section.selector
                  );
                }
              });
              
              // If sections weren't in the response, fetch them
              if (!cartData.sections || Object.keys(cartData.sections).length === 0) {
                const sectionsToRender = this.getSectionsToRender();
                const sectionIds = sectionsToRender.map(s => s.section || s.id).filter(Boolean);
                if (sectionIds.length > 0) {
                  const sectionsUrl = `${window.Shopify?.routes?.root || '/'}cart?${sectionIds.map(id => `section_id=${id}`).join('&')}`;
                  const sectionsResponse = await fetch(sectionsUrl);
                  
                  if (sectionsResponse.ok) {
                    const sectionsText = await sectionsResponse.text();
                    const parser = new DOMParser();
                    const sectionsDoc = parser.parseFromString(sectionsText, 'text/html');
                    
                    sectionsToRender.forEach((section) => {
                      const sectionElement = sectionsDoc.querySelector(`#shopify-section-${section.section}`) ||
                                            sectionsDoc.querySelector(`[id*="${section.section}"]`) ||
                                            sectionsDoc.querySelector(`.shopify-section[id*="${section.section}"]`);
                      
                      if (sectionElement) {
                        const elementToReplace =
                          document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
                        if (elementToReplace) {
                          elementToReplace.innerHTML = this.getSectionInnerHTML(
                            sectionElement.outerHTML,
                            section.selector
                          );
                        }
                      }
                    });
                  }
                }
              }
              
              // Publish cart update event
              publish(PUB_SUB_EVENTS.cartUpdate, { 
                source: 'cart-cleanup', 
                cartData: cartData 
              });
            }
          } catch (error) {
            // Error removing item
          }
        };
        
        // Cleanup function - runs directly in cart.js to avoid dependency issues
        const cleanupOrphanedColorShells = async () => {
          if (typeof window.cleanupOrphanedColorShells === 'function') {
            await window.cleanupOrphanedColorShells();
            return;
          }
          
          try {
            const cartResponse = await fetch('/cart.js?t=' + Date.now());
            if (!cartResponse.ok) {
              return;
            }

            const cart = await cartResponse.json();

            const colorShellItems = cart.items.filter(item => isColorShellItem(item));

            if (colorShellItems.length === 0) {
              return;
            }

            // Create a set of cloak holster items in the cart
            const cloakHolsterItems = cart.items.filter(item => isEligibleMainProduct(item));

            // If there are NO cloak holsters, remove ALL color shells
            if (cloakHolsterItems.length === 0) {
              for (const colorShell of colorShellItems) {
                await removeCartItemByKey(colorShell.key);
                await new Promise(resolve => setTimeout(resolve, 200));
              }
              return;
            }

            // Create a set of ALL item keys and stable keys (product_id:variant_id) in the cart
            const allCartItemKeys = new Set();
            cart.items.forEach(item => {
              allCartItemKeys.add(item.key);
              allCartItemKeys.add(`${item.product_id}:${item.variant_id}`);
            });

            // Group shells by their main product key
            const shellsByMainProduct = new Map();

            for (const colorShell of colorShellItems) {
              // Get the main product key from properties
              let mainProductKey = null;
              if (colorShell.properties) {
                if (Array.isArray(colorShell.properties)) {
                  const mainProductProp = colorShell.properties.find(prop => prop.name === '_main_product_key');
                  mainProductKey = mainProductProp ? mainProductProp.value : null;
                } else if (typeof colorShell.properties === 'object') {
                  mainProductKey = colorShell.properties._main_product_key || null;
                }
              }

              // Fallback to localStorage if no key in properties
              if (!mainProductKey) {
                try {
                  const localData = localStorage.getItem('cloak-color-shell-metadata');
                  if (localData) {
                    const metadata = JSON.parse(localData);
                    mainProductKey = metadata[colorShell.key]?.mainProductKey || null;
                  }
                } catch (e) {}
              }

              // If no main product key or main product doesn't exist, remove the color shell
              if (!mainProductKey || mainProductKey.trim() === '' || !allCartItemKeys.has(mainProductKey)) {
                await removeCartItemByKey(colorShell.key);
                await new Promise(resolve => setTimeout(resolve, 200));
              } else {
                // Group by main product key for quantity checking
                if (!shellsByMainProduct.has(mainProductKey)) {
                  shellsByMainProduct.set(mainProductKey, []);
                }
                shellsByMainProduct.get(mainProductKey).push(colorShell);
              }
            }

            // Check if we have too many shells for each main product
            for (const [mainProductKey, shells] of shellsByMainProduct.entries()) {
              const mainProduct = cart.items.find(item => item.key === mainProductKey || `${item.product_id}:${item.variant_id}` === mainProductKey);
              if (mainProduct) {
                const mainProductQuantity = mainProduct.quantity || 1;
                const shellCount = shells.length;

                if (shellCount > mainProductQuantity) {
                  // Too many shells - remove the extra ones (keep only as many as quantity)
                  // Keep the last N shells (most recent) and remove the oldest ones
                  const shellsToKeep = shells.slice(-mainProductQuantity); // Keep last N shells
                  const shellsToRemove = shells.slice(0, shellCount - mainProductQuantity); // Remove first (oldest) shells

                  for (const shellToRemove of shellsToRemove) {
                    await removeCartItemByKey(shellToRemove.key);
                    await new Promise(resolve => setTimeout(resolve, 200));
                  }
                }
              }
            }
          } catch (error) {
            // Error in cleanup
          }
        };
        
        // For deletions, only run cleanup if it's a cloak holster (3.5 single or chest)
        // Skip cleanup if color shells are being added (to prevent removing shells that are being added)
        if (isAddingColorUpgrade) {
          // Color upgrade being added, skip cleanup
        } else if (itemCountIncreased && !isDeletion) {
          // Item count increased - this might be a product addition
          // Wait longer before cleanup to ensure all additions (including color shells) are complete
          setTimeout(() => cleanupOrphanedColorShells(), 2000);
        } else if (isDeletion && isDeletedItemCloakHolster) {
          // Cloak holster deleted, run cleanup multiple times
          setTimeout(() => cleanupOrphanedColorShells(), 100);
          setTimeout(() => cleanupOrphanedColorShells(), 600);
          setTimeout(() => cleanupOrphanedColorShells(), 1200);
          setTimeout(() => cleanupOrphanedColorShells(), 2000);
        } else if (isDeletion && !isDeletedItemCloakHolster) {
          // Non-cloak item deleted, skip cleanup
        } else {
          // For updates (quantity changes), run cleanup once with a delay
          setTimeout(() => cleanupOrphanedColorShells(), 1000);
        }

        // Also try to call the global function if it exists (for compatibility)
        // But skip it if we're adding color upgrades or if it's not a cloak holster deletion
        if (typeof window.cleanupOrphanedColorShells === 'function' && !isAddingColorUpgrade) {
          if (isDeletion && isDeletedItemCloakHolster) {
            setTimeout(() => window.cleanupOrphanedColorShells(), 100);
            setTimeout(() => window.cleanupOrphanedColorShells(), 600);
          } else if (itemCountIncreased && !isDeletion) {
            // Wait longer for additions
            setTimeout(() => window.cleanupOrphanedColorShells(), 2000);
          } else if (!isDeletion) {
            // For quantity updates, run cleanup
            setTimeout(() => window.cleanupOrphanedColorShells(), 1000);
          }
        }
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  removeColorShells(deletedItemKey = null) {
    // Remove color shells linked to the deleted holster
    fetch('/cart.js?t=' + Date.now())
      .then(r => r.json())
      .then(cart => {
        if (!cart.items) return;

        // Find color shells linked to the deleted item
        const shellsToRemove = cart.items.filter((item) => {
          const handle = item.product_handle || item.handle || '';
          const title = item.title || '';
          const sku = item.sku || '';

          // Color products have these handles
          const isColorProduct = handle.startsWith('cloak-') && (
            handle.includes('color') ||
            handle.includes('mc-') ||
            handle === 'cloak-recon-haze-topo' ||
            handle === 'cloak-arctic-white' ||
            handle === 'cloak-safety-yellow' ||
            handle === 'cloak-neon-pink' ||
            handle === 'cloak-safety-orange' ||
            handle === 'cloak-purple' ||
            handle === 'cloak-police-blue' ||
            handle === 'cloak-army-green' ||
            handle === 'cloak-black-carbon-fiber' ||
            handle === 'cloak-dark-grey'
          );

          const isColorShellSku = sku === '102010' || sku === '102011' || sku === '100401' || sku === '300037' ||
                                sku.startsWith('1020') || sku.startsWith('1004') || sku.startsWith('3000');

          const isTitleBasedColorShell = title.toLowerCase().includes('color upgrade') ||
                                        title.toLowerCase().includes('color shell');

          let isColorShell = isColorProduct || isColorShellSku || isTitleBasedColorShell;

          if (!isColorShell) {
            return false;
          }

          // If we have a deletedItemKey, only remove shells linked to that specific item
          if (deletedItemKey) {
            let mainProductKey = null;

            // Properties might be an array or object
            if (item.properties) {
              if (Array.isArray(item.properties)) {
                const mainProductProp = item.properties.find(prop => prop.name === '_main_product_key');
                mainProductKey = mainProductProp ? mainProductProp.value : null;
              } else if (typeof item.properties === 'object') {
                mainProductKey = item.properties._main_product_key || null;
              }
            }

            // Match found
            if (mainProductKey === deletedItemKey) {
              return true;
            }

            // If _main_product_key doesn't match or isn't set, use fallback: remove as orphaned
            if (!mainProductKey) {
              return true;
            }

            return false;
          }

          // If no deletedItemKey provided, remove all color shells (fallback behavior)
          return true;
        });

        // Remove each color shell
        if (shellsToRemove.length > 0) {
          let removedCount = 0;
          shellsToRemove.forEach((shell) => {
            const index = cart.items.indexOf(shell) + 1;
            fetch(`${routes.cart_change_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                line: index,
                quantity: 0,
                sections: this.getSectionsToRender().map(section => section.section),
                sections_url: window.location.pathname,
              })
            }).then(() => {
              removedCount++;
              // Refresh page after all shells are removed
              if (removedCount === shellsToRemove.length) {
                setTimeout(() => {
                  window.location.reload();
                }, 300);
              }
            });
          });
        } else {
          window.holsterBeingRemoved = false;
          window.deletedHolsterKey = null;
        }
      })
      .catch(err => {
        console.error('[Cart] Error in removeColorShells:', err);
        window.holsterBeingRemoved = false;
        window.deletedHolsterKey = null;
      });
  }

  removeHolster(mainProductKey) {
    if (!mainProductKey) {
      window.shellBeingRemoved = false;
      window.deletedMainProductKey = null;
      return;
    }
    console.log('[Cart] removeHolster: Searching for holster with key/stable key', mainProductKey);
    fetch('/cart.js?t=' + Date.now())
      .then(r => r.json())
      .then(cart => {
        if (!cart.items) {
          window.shellBeingRemoved = false;
          window.deletedMainProductKey = null;
          return;
        }
        
        // Find the holster item matching the key
        const holsterToRemove = cart.items.find(item => {
          const stableKey = `${item.product_id}:${item.variant_id}`;
          return item.key === mainProductKey || stableKey === mainProductKey;
        });

        if (holsterToRemove) {
          console.log('[Cart] removeHolster: Found holster to remove', holsterToRemove.key, holsterToRemove.title);
          const index = cart.items.indexOf(holsterToRemove) + 1;
          fetch(`${routes.cart_change_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              line: index,
              quantity: 0,
              sections: this.getSectionsToRender().map(section => section.section),
              sections_url: window.location.pathname
            })
          })
          .then(() => {
            console.log('[Cart] removeHolster: Successfully removed holster, reloading page...');
            setTimeout(() => {
              window.location.reload();
            }, 300);
          });
        } else {
          console.log('[Cart] removeHolster: Holster not found in cart matching', mainProductKey);
          window.shellBeingRemoved = false;
          window.deletedMainProductKey = null;
        }
      })
      .catch(err => {
        console.error('[Cart] Error in removeHolster:', err);
        window.shellBeingRemoved = false;
        window.deletedMainProductKey = null;
      });
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Color Product Banner - Display banner if any cart item has custom.is_color_product metafield
(function() {
  const BANNER_ID = 'color-product-cart-banner';
  
  // Create banner element
  function createBanner() {
    const bannerContainer = document.createElement('div');
    bannerContainer.id = BANNER_ID;
    bannerContainer.className = 'color-product-cart-banner';
    
    // Add responsive styles via style element
    if (!document.getElementById('color-product-banner-styles')) {
      const style = document.createElement('style');
      style.id = 'color-product-banner-styles';
      style.textContent = `
        .color-product-cart-banner {
          width: 50%;
          max-width: 600px;
          margin: 0;
          margin-top: 2rem !important;
          margin-bottom: 1rem;
          padding-top: 1rem;
        }
        @media (max-width: 749px) {
          .color-product-cart-banner {
            width: 100%;
            max-width: 100%;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    bannerContainer.style.display = 'none';
    
    const bannerLink = document.createElement('a');
    bannerLink.href = '/products/cloak-shells';
    bannerLink.style.cssText = `
      display: block;
      width: 100%;
      height: auto;
      text-decoration: none;
      cursor: pointer;
      margin-bottom: 0;
      padding: 0;
    `;
    
    // Create the image element
    const bannerImage = document.createElement('img');
    bannerImage.src = 'https://cdn.shopify.com/s/files/1/0881/4854/1726/files/Cloak_Colors_All-1.jpg?v=1763162867';
    bannerImage.alt = 'Buy Additional Colors';
    bannerImage.style.cssText = `
      display: block;
      width: 100%;
      height: 200px;
      object-fit: cover;
      max-width: 100%;
    `;
    
    bannerLink.appendChild(bannerImage);
    
    const bannerText = document.createElement('div');
    bannerText.textContent = 'Buy Additional Colors ';
    bannerText.style.cssText = `
      margin-top: 0.5rem;
      text-align: left;
      display: block;
      position: static;
      width: 100%;
      clear: both;
    `;
    
    bannerContainer.appendChild(bannerLink);
    bannerContainer.appendChild(bannerText);
    
    return bannerContainer;
  }
  
  // Check if any cart item has the custom.is_color_product metafield
  async function checkForColorProduct() {
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        return false;
      }

      const cart = await cartResponse.json();

      // Check each item for the metafield
      for (const item of cart.items) {
        
        // Try to get product metafields - fetch product page HTML and look for data attribute
        // Since GraphQL requires auth and product JSON doesn't include metafields,
        // we fetch the product page and look for a data attribute or check the handle pattern
        if (item.product_id && item.handle) {
          try {
            // First, check if handle matches color product pattern (cloak-* but not cloak-color or test-cloak-shell)
            // This is a fallback - ideally we'd check the metafield from the page
            const isColorProductHandle = item.handle.startsWith('cloak-') &&
                                        item.handle !== 'cloak-color' &&
                                        item.handle !== 'test-cloak-shell';

            // Fetch the product page HTML to check for metafield data attribute
            const productPageUrl = `/products/${item.handle}`;

            const productResponse = await fetch(productPageUrl);
            if (productResponse.ok) {
              const html = await productResponse.text();
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');

              // Look for data attribute with metafield value
              // Check product-media-container or any element with data-is-color-product
              const productContainer = doc.querySelector('[data-is-color-product]') ||
                                     doc.querySelector('.product-media-container[data-is-color-product]') ||
                                     doc.querySelector(`[data-product-id="${item.product_id}"][data-is-color-product]`);

              if (productContainer) {
                const isColorProduct = productContainer.getAttribute('data-is-color-product');

                // Check if it's true
                if (isColorProduct === 'true' || isColorProduct === 'True' || isColorProduct === '1') {
                  return true;
                }
              }

              // Fallback: check handle pattern if no data attribute found
              if (isColorProductHandle) {
                return true;
              }
            } else {
              // Fallback to handle pattern if fetch fails
              if (isColorProductHandle) {
                return true;
              }
            }
          } catch (e) {
            // Fallback to handle pattern on error
            const isColorProductHandle = item.handle &&
                                        item.handle.startsWith('cloak-') &&
                                        item.handle !== 'cloak-color' &&
                                        item.handle !== 'test-cloak-shell';
            if (isColorProductHandle) {
              return true;
            }
          }
        }
      }

      return false;
    } catch (error) {
      return false;
    }
  }
  
  // Show or hide banner based on cart contents
  async function updateBanner() {
    const hasColorProduct = await checkForColorProduct();

    let banner = document.getElementById(BANNER_ID);

    if (!banner) {
      // Banner doesn't exist, create it
      // Try cart drawer first (for drawer cart)
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer) {
        // Place banner right after cart items (before footer)
        const cartItems = cartDrawer.querySelector('cart-drawer-items');

        if (cartItems) {
          // Add a spacer div before the banner to ensure spacing
          const spacer = document.createElement('div');
          spacer.style.cssText = 'height: 2rem; width: 100%;';
          const newBanner = createBanner();
          cartItems.insertAdjacentElement('afterend', spacer);
          spacer.insertAdjacentElement('afterend', newBanner);
          banner = newBanner;
        } else {
          // Fallback - try to find the cart items table
          const cartItemsTable = cartDrawer.querySelector('.cart-items') || cartDrawer.querySelector('table');
          if (cartItemsTable) {
            const newBanner = createBanner();
            cartItemsTable.insertAdjacentElement('afterend', newBanner);
            banner = newBanner;
          } else {
            // Last resort - append to cart drawer itself
            const newBanner = createBanner();
            cartDrawer.appendChild(newBanner);
            banner = newBanner;
          }
        }
      } else {
        // Try cart page (main-cart-items or cart form)
        const cartForm = document.querySelector('form[action="/cart"]') || document.querySelector('form[action*="cart"]');
        const mainCartItems = document.querySelector('main-cart-items') || document.querySelector('#main-cart-items');
        const cartItemsTable = document.querySelector('.cart-items') || document.querySelector('table.cart-items');

        // Place banner right after cart items (before footer)
        if (mainCartItems) {
          // Add a spacer div before the banner to ensure spacing
          const spacer = document.createElement('div');
          spacer.style.cssText = 'height: 2rem; width: 100%;';
          const newBanner = createBanner();
          mainCartItems.insertAdjacentElement('afterend', spacer);
          spacer.insertAdjacentElement('afterend', newBanner);
          banner = newBanner;
        } else if (cartItemsTable) {
          const newBanner = createBanner();
          cartItemsTable.insertAdjacentElement('afterend', newBanner);
          banner = newBanner;
        } else if (cartForm) {
          const newBanner = createBanner();
          cartForm.insertAdjacentElement('afterend', newBanner);
          banner = newBanner;
        } else {
          // Last resort - try to find any cart-related element
          const cartSection = document.querySelector('[id*="cart"]') || document.querySelector('.cart');
          if (cartSection) {
            const newBanner = createBanner();
            cartSection.appendChild(newBanner);
            banner = newBanner;
          } else {
            return;
          }
        }
      }
    }

    if (banner) {
      // Banner exists, show or hide it
      banner.style.display = hasColorProduct ? 'block' : 'none';
    }
  }
  
  // Initialize banner on page load
  function initBanner() {
    // Function to try updating banner with retries
    function tryUpdateBanner(retries = 5) {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartForm = document.querySelector('form[action="/cart"]') || document.querySelector('form[action*="cart"]');
      const mainCartItems = document.querySelector('main-cart-items') || document.querySelector('#main-cart-items');

      if (!cartDrawer && !cartForm && !mainCartItems && retries > 0) {
        setTimeout(() => tryUpdateBanner(retries - 1), 500);
        return;
      }

      updateBanner();
    }

    // Wait a bit for cart to be ready, then try with retries
    setTimeout(() => {
      tryUpdateBanner();
    }, 500);

    // Also try when cart drawer opens
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Listen for when cart drawer opens
      const observer = new MutationObserver((mutations) => {
        if (cartDrawer.classList.contains('active')) {
          setTimeout(updateBanner, 300);
        }
      });
      observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    }

    // Listen for cart updates
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(updateBanner, 300);
      });
    }

    // Also listen for custom cart events
    document.addEventListener('cart:updated', () => {
      setTimeout(updateBanner, 300);
    });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBanner);
  } else {
    initBanner();
  }
})();

// Override Axon prices in cart drawer and cart page
(function() {
  function overrideAxonPrices() {
    const rows = document.querySelectorAll('.cart-item');
    rows.forEach(row => {
      const rowText = row.textContent.toLowerCase();
      const isLevelII = rowText.includes('level ii') || rowText.includes('level 2');
      const hasAxon = Array.from(row.querySelectorAll('.product-option')).some(el => {
        const text = el.textContent.toLowerCase();
        return text.includes('axon signal') && text.includes('yes');
      });

      if (isLevelII && hasAxon) {
        const qtyInput = row.querySelector('input.quantity__input');
        const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        const targetPriceText = '$' + (134.99 * qty).toFixed(2);

        const priceWrapper = row.querySelector('.cart-item__price-wrapper');
        if (priceWrapper) {
          const priceEnd = priceWrapper.querySelector('.price--end');
          if (priceEnd) {
            if (priceEnd.textContent.trim() !== targetPriceText) {
              priceEnd.textContent = targetPriceText;
            }
          } else {
            if (priceWrapper.textContent.trim() !== targetPriceText) {
              priceWrapper.innerHTML = `<span class="price price--end">${targetPriceText}</span>`;
            }
          }
        }
      }
    });
  }

  function observeCartDOM() {
    const observer = new MutationObserver(() => {
      overrideAxonPrices();
    });
    const cartDrawer = document.getElementById('CartDrawer') || document.querySelector('cart-drawer');
    const cartItems = document.querySelector('cart-items');
    if (cartDrawer) observer.observe(cartDrawer, { childList: true, subtree: true });
    if (cartItems) observer.observe(cartItems, { childList: true, subtree: true });
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      overrideAxonPrices();
      observeCartDOM();
    });
  } else {
    overrideAxonPrices();
    observeCartDOM();
  }

  // Listen to Shopify pubsub cart updates
  if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      setTimeout(overrideAxonPrices, 100);
      setTimeout(overrideAxonPrices, 400);
    });
  }

  // Listen to standard cart events
  document.addEventListener('cart:updated', () => {
    setTimeout(overrideAxonPrices, 100);
    setTimeout(overrideAxonPrices, 400);
  });
})();
